import { useState } from 'react';
import { Link2, CheckCircle } from 'lucide-react';
import { useLtiAuth } from '@/hooks/useLtiAuth';
import './LtiDeepLinkPage.css';

interface DlQuiz {
  id: string;
  title: string;
}

async function fetchDeepLinkQuizzes(dlToken: string): Promise<DlQuiz[]> {
  const resp = await fetch(
    `/api/lti/deep-link-quizzes?dl_token=${encodeURIComponent(dlToken)}`,
  );
  if (!resp.ok) throw new Error('Failed to load assessments');
  return resp.json() as Promise<DlQuiz[]>;
}

async function submitDeepLinkResponse(
  dlToken: string,
  quizId: string,
  quizTitle: string,
): Promise<string> {
  const resp = await fetch('/assess/api/lti/deep-link-response', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dl_token: dlToken, quiz_id: quizId, quiz_title: quizTitle }),
  });
  if (!resp.ok) throw new Error('Failed to create deep link response');
  return resp.text();
}

/**
 * Deep Linking quiz picker page.
 *
 * Reached after an LTI Deep Linking launch via:
 *   /lti/deep-link?lti_code=<one-time>&dl_token=<context>
 *
 * Steps:
 *  1. useLtiAuth exchanges lti_code → stores real JWT tokens → removes lti_code from URL
 *  2. dl_token remains in URL; we use it to fetch available quizzes
 *  3. On quiz selection we POST dl_token + quiz_id → backend returns auto-submit HTML
 *  4. We write that HTML to document — browser POSTs the signed JWT back to the LMS
 */
export function LtiDeepLinkPage() {
  const { isExchanging, error: authError, done: authDone } = useLtiAuth();

  const [quizzes, setQuizzes] = useState<DlQuiz[] | null>(null);
  const [quizzesError, setQuizzesError] = useState<string | null>(null);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [selected, setSelected] = useState<DlQuiz | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const params = new URLSearchParams(window.location.search);
  const dlToken = params.get('dl_token') ?? '';

  // Load quiz list once auth exchange is done
  if (authDone && quizzes === null && !loadingQuizzes && !quizzesError && dlToken) {
    setLoadingQuizzes(true);
    fetchDeepLinkQuizzes(dlToken)
      .then((data) => {
        setQuizzes(data);
        setLoadingQuizzes(false);
      })
      .catch((err: unknown) => {
        setQuizzesError(err instanceof Error ? err.message : 'Failed to load assessments');
        setLoadingQuizzes(false);
      });
  }

  function handleSelect(quiz: DlQuiz) {
    setSelected(quiz);
    setSubmitError(null);
  }

  function handleEmbed() {
    if (!selected || !dlToken || submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    submitDeepLinkResponse(dlToken, selected.id, selected.title)
      .then((html) => {
        // Parse the server-provided HTML, extract the JWT and form action,
        // then create and submit a fresh form — avoids document.write deprecation.
        const parser = new DOMParser();
        const parsed = parser.parseFromString(html, 'text/html');
        const srcForm = parsed.getElementById('f') as HTMLFormElement | null;
        const srcInput = parsed.querySelector('input[name="JWT"]') as HTMLInputElement | null;

        if (srcForm && srcInput) {
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = srcForm.action;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'JWT';
          input.value = srcInput.value;
          form.appendChild(input);
          document.body.appendChild(form);
          form.submit();
        } else {
          setSubmitError('Unexpected response from server');
          setSubmitting(false);
        }
      })
      .catch((err: unknown) => {
        setSubmitError(err instanceof Error ? err.message : 'Failed to create deep link');
        setSubmitting(false);
      });
  }

  if (!dlToken) {
    return (
      <div className="dl-page dl-page--error">
        <Link2 size={40} />
        <p>Invalid deep link session — no token provided.</p>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="dl-page dl-page--error">
        <Link2 size={40} />
        <p>Authentication failed: {authError}</p>
      </div>
    );
  }

  if (isExchanging || !authDone) {
    return (
      <div className="dl-page dl-page--loading">
        <div className="dl-spinner" />
        <p>Authenticating…</p>
      </div>
    );
  }

  if (quizzesError) {
    return (
      <div className="dl-page dl-page--error">
        <Link2 size={40} />
        <p>Failed to load assessments: {quizzesError}</p>
      </div>
    );
  }

  if (loadingQuizzes || quizzes === null) {
    return (
      <div className="dl-page dl-page--loading">
        <div className="dl-spinner" />
        <p>Loading assessments…</p>
      </div>
    );
  }

  return (
    <div className="dl-page">
      <div className="dl-header">
        <Link2 size={22} />
        <div>
          <h1 className="dl-title">Select an Assessment to Embed</h1>
          <p className="dl-subtitle">
            Choose a quiz from this academy to embed as an LMS assignment.
          </p>
        </div>
      </div>

      {quizzes.length === 0 ? (
        <div className="dl-empty">
          <p>No assessments available in this academy.</p>
        </div>
      ) : (
        <div className="dl-list">
          {quizzes.map((q) => (
            <button
              key={q.id}
              type="button"
              className={`dl-quiz-row${selected?.id === q.id ? ' dl-quiz-row--selected' : ''}`}
              onClick={() => handleSelect(q)}
            >
              <span className="dl-quiz-title">{q.title}</span>
              {selected?.id === q.id && <CheckCircle size={16} className="dl-check" />}
            </button>
          ))}
        </div>
      )}

      {submitError && <p className="dl-error-msg">{submitError}</p>}

      {selected && (
        <div className="dl-actions">
          <button
            type="button"
            className="dl-btn dl-btn--primary"
            disabled={submitting}
            onClick={handleEmbed}
          >
            {submitting ? 'Embedding…' : `Embed "${selected.title}"`}
          </button>
          <button
            type="button"
            className="dl-btn"
            onClick={() => setSelected(null)}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
